import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { useSaved } from "../context/SavedContext.jsx";
import { formatDisplayPrice } from "../utils/currency.js";
import { HEADER_PANEL_EVENT, dispatchHeaderPanelEvent } from "../utils/headerEvents.js";

const formatListingPrice = (listing) => {
  const usd = formatDisplayPrice(listing?.price_usd, "USD");
  if (usd) {
    return usd;
  }
  const fallback = formatDisplayPrice(
    typeof listing?.price === "number" ? listing.price : null,
    typeof listing?.currency === "string" && listing.currency.trim() ? listing.currency : "USD",
  );
  return fallback || "Price unknown";
};

export default function SavedWidget() {
  const {
    items,
    isOpen,
    togglePanel,
    closePanel,
    removeItem,
    clearAll,
  } = useSaved();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const bubbleTimerRef = useRef(null);
  const [showCountBubble, setShowCountBubble] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDown = (event) => {
      const panelNode = panelRef.current;
      const hostNode = containerRef.current;
      if (panelNode && panelNode.contains(event.target)) {
        return;
      }
      if (hostNode && hostNode.contains(event.target)) {
        return;
      }
      closePanel();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closePanel();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closePanel]);

  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (bubbleTimerRef.current) {
      clearTimeout(bubbleTimerRef.current);
      bubbleTimerRef.current = null;
    }
    if (items.length > 0) {
      setShowCountBubble(true);
      bubbleTimerRef.current = setTimeout(() => {
        setShowCountBubble(false);
        bubbleTimerRef.current = null;
      }, 2000);
    } else {
      setShowCountBubble(false);
    }
    return () => {
      if (bubbleTimerRef.current) {
        clearTimeout(bubbleTimerRef.current);
        bubbleTimerRef.current = null;
      }
    };
  }, [items.length]);

  const handleViewMore = useCallback(() => {
    if (!items.length) {
      return;
    }
    closePanel();
    navigate("/featured/saved");
  }, [items.length, closePanel, navigate]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.addEventListener !== "function") {
      return undefined;
    }
    const handlePanelOpen = (event) => {
      if (event?.detail !== "saved") {
        closePanel();
      }
    };
    window.addEventListener(HEADER_PANEL_EVENT, handlePanelOpen);
    return () => {
      window.removeEventListener(HEADER_PANEL_EVENT, handlePanelOpen);
    };
  }, [closePanel]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    dispatchHeaderPanelEvent("saved");
  }, [isOpen]);

  const totalLabel = useMemo(() => `${items.length} saved`, [items.length]);

  const buttonLabel =
    items.length > 0
      ? `Open saved items, ${items.length} item${items.length === 1 ? "" : "s"}`
      : "Open saved items";
  const countDigits = items.length > 0 ? `${items.length}`.length : 0;

  return (
    <div
      className={isOpen ? "saved-widget saved-widget--open" : "saved-widget"}
      ref={containerRef}
    >
      <button
        type="button"
        className="saved-widget__button"
        data-header-interaction="saved"
        onClick={togglePanel}
        aria-label={buttonLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen ? "true" : "false"}
      >
        <span className="saved-widget__icon" aria-hidden="true">
          <svg
            className="saved-widget__icon-svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 20.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 3.99 4 6.5 4c1.54 0 3.04 0.99 3.57 2.36h0.87C11.46 4.99 12.96 4 14.5 4 17.01 4 19 6 19 8.5c0 3.78-3.4 6.86-8.55 10.54z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>
      {showCountBubble && items.length > 0 && (
        <span
          key={`${items.length}-${countDigits}`}
          className="saved-widget__count saved-widget__count--bubble"
          data-digit-count={countDigits}
          aria-label={`${items.length} item${items.length === 1 ? "" : "s"} saved`}
        >
          {items.length}
        </span>
      )}
      {isOpen && (
        <div
          className="saved-widget__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Saved listings"
          tabIndex={-1}
          ref={panelRef}
        >
          <header className="saved-widget__header">
            <div className="saved-widget__header-group">
              <p className="saved-widget__header-title">Saved</p>
              <p className="saved-widget__header-subtitle">{totalLabel}</p>
            </div>
            <div className="saved-widget__header-actions">
              <button
                type="button"
                className="saved-widget__header-close"
                onClick={closePanel}
                aria-label="Close saved items panel"
              >
                {"\u00D7"}
              </button>
              {items.length > 0 && (
                <button
                  type="button"
                  className="saved-widget__header-clear"
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      const confirmed = window.confirm("Clear all saved listings?");
                      if (!confirmed) {
                        return;
                      }
                    }
                    clearAll();
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          </header>
          <div className="saved-widget__body">
            {items.length === 0 ? (
              <p className="saved-widget__empty">You haven&rsquo;t saved any listings yet.</p>
            ) : (
              <ul className="saved-widget__list">
                {items.map((entry) => {
                  const listing = entry.item ?? {};
                  const priceLabel = formatListingPrice(listing);
                  return (
                    <li className="saved-widget__item" key={entry.id}>
                      <div className="saved-widget__thumb">
                        {listing.image_url ? (
                          <img 
                            src={listing.image_url.includes("static.mercdn.net")
                              ? `/api/images/proxy?url=${encodeURIComponent(listing.image_url)}`
                              : listing.image_url
                            } 
                            alt="" 
                          />
                        ) : (
                          <span className="saved-widget__placeholder">No image</span>
                        )}
                      </div>
                      <div className="saved-widget__meta">
                        <p className="saved-widget__title">{listing.title || "Untitled listing"}</p>
                        <p className="saved-widget__price">{priceLabel}</p>
                      </div>
                      <button
                        type="button"
                        className="saved-widget__remove-x"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeItem(entry.id);
                        }}
                        aria-label="Remove from saved"
                        title="Remove from saved"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <footer className="saved-widget__footer">
            <button
              type="button"
              className="saved-widget__view-more"
              onClick={handleViewMore}
              disabled={items.length === 0}
            >
              View more
            </button>
          </footer>
        </div>
      )}
    </div>
  );
}
